'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Ne yapmıyoruz" — dört bilinçli sınır.
 *
 * Dört maddenin (principles) hemen ALTINDA ve onun ikizi: o bölüm neyin her
 * zaman yapıldığını söylüyor, burası neyin hiç yapılmadığını. İkisi ayrı
 * bölüm çünkü ayrı iki soruya cevap veriyorlar ve tek bir listede toplanınca
 * "yapıyoruz" ile "yapmıyoruz" aynı tonda okunuyor.
 *
 * ── Biçim bilgi taşıyor ───────────────────────────────────────────────────
 *
 * Her satır bir ÇİFT: solda reddedilen şey, sağda onun yerine yapılan. Tek
 * sütunlu bir liste "yapmıyoruz" derdi ve orada kalırdı; çift, cümleyi
 * tamamlıyor. Bu yüzden yerleşim iki sütun ve okuma yönü soldan sağa —
 * ayraç da o yönü gösteriyor.
 *
 * Reddedilen taraf mürekkep olarak DAHA HAFİF, cevap tarafı daha koyu:
 * sayfanın söylediği şey sağdaki. Sol taraf yalnızca soruyu kuruyor.
 *
 * ── Metin uydurma değil ───────────────────────────────────────────────────
 *
 * Dördü de ürünün gerçek duruşu. Özellikle `promise` maddesi bir kural
 * koyuyor: sayfaya rakamlı iddia yazılmıyor. messages → `about._DEMO` bunu
 * tekrar ediyor — bir yerde "%40 ROAS" yazmak bu maddeyi yalanlar.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Satır ayracı SOLDAN çekiliyor, sonra sol taraf, sonra sağ taraf giriyor.
 * Sıra cümlenin sırası: "şunu yapmıyoruz" → "bunun yerine şunu". Gecikme
 * kasten `stagger.base`, satır içi `stagger.tight`: satırlar birbirini
 * bekliyor ama çiftin iki yarısı neredeyse birlikte geliyor.
 *
 * Blur yalnızca cevap taraflarında (4 öğe) — göz sağa odaklanıyor.
 *
 * Döngü ve parallax yok.
 *
 * Giriş hareketi YOK (kullanıcı kararı, 9 Eyl 2026): bölüm sayfa açılır
 * açılmaz son hâlinde basılıyor (`initial={false}`). Eskiden `whileInView` +
 * `once: true` ile kaydırdıkça açılıyordu; site sahipleri sayfanın yüklenmiş
 * gelmesini istedi. Variant'lar yerinde — geri istenirse tetik tek satır.
 */

/** Sıra: en yaygın yanlış anlamadan en incesine. */
const ITEMS = ['agency', 'blackbox', 'lockin', 'promise'] as const;

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

/* Sol sütun tek parça girer — etiket, başlık ve giriş bölünmez. */
const asideV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const listV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.06 } },
};

const rowV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

const ruleV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: {
    opacity: 1,
    scaleX: 1,
    transition: { duration: dur.slow, ease: ease.out },
  },
};

const notV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const insteadV: Variants = {
  hidden: { opacity: 0, y: 8, filter: 'blur(5px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

export function AboutLimits() {
  const t = useTranslations('about.limits');
  const uid = useId();

  return (
    <section className="nope" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="nope-inner" variants={sectionV} initial={false} animate="show">
            <div className="nope-layout">
              <motion.div className="nope-aside" variants={asideV}>
                <p className="nope-eyebrow">{t('eyebrow')}</p>
                <h2 className="nope-title" id={`${uid}-title`}>
                  {t.rich('title', { hl })}
                </h2>
                <p className="nope-lead">{t('lead')}</p>
              </motion.div>

              <motion.ul className="nope-list" variants={listV}>
                {ITEMS.map((key) => (
                  <motion.li className="nope-row" key={key} variants={rowV}>
                    <motion.span className="nope-rule" aria-hidden="true" variants={ruleV} />

                    <motion.div className="nope-not" variants={notV}>
                      {/* Etiketler ekran okuyucuya da gerekli: iki sütunun
                          hangisinin ret hangisinin cevap olduğunu görsel
                          hiyerarşi söylüyor, metin söylemiyordu. */}
                      <span className="nope-tag">{t('notLabel')}</span>
                      <h3 className="nope-not-text">{t(`items.${key}.not`)}</h3>
                    </motion.div>

                    <motion.div className="nope-instead" variants={insteadV}>
                      <span className="nope-tag nope-tag--yes">{t('insteadLabel')}</span>
                      <p className="nope-instead-text">{t(`items.${key}.instead`)}</p>
                    </motion.div>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
